import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_CLIP_DATA } from '@/queries/queries';
import { useFlushOnPageHide } from './use-flush-on-page-hide';
import { normalizeVideoEffects, type VideoEffects } from '@/lib/video-effects/types';

type Write = { clipId: string; videoEffects: VideoEffects; revision: number };
type SavedData = { videoEffects: VideoEffects; videoEffectsRevision: number };
type Options = {
  clipId: string | null; loadedClipId: string | null; videoEffects: VideoEffects;
  loadedRevision?: number; enabled: boolean; delay?: number;
  onSaved?: (clipId: string, data: SavedData) => void;
};
let lastRevision = 0;
const nextRevision = (loaded = 0) => {
  lastRevision = Math.max(Date.now() * 1000, lastRevision + 1, Number.isSafeInteger(loaded) ? loaded + 1 : 0);
  return lastRevision;
};
/** Serial ordinary writes plus immediate, revision-guarded unload snapshots.
 * The backend compares revisions atomically, so an older fetch completing
 * after a pagehide keepalive cannot put the previous look back. */
export function useAutoSaveVideoEffects({ clipId, loadedClipId, loadedRevision, videoEffects, enabled, delay = 500, onSaved }: Options) {
  const [mutate, { loading: saving }] = useMutation(UPDATE_CLIP_DATA, { fetchPolicy: 'no-cache' });
  const [error, setError] = useState(false);
  const pending = useRef(new Map<string, Write>());
  const latest = useRef(new Map<string, Write>());
  const acknowledged = useRef(new Map<string, number>());
  const inFlight = useRef(new Map<string, Promise<void>>());
  const keepalives = useRef(new Map<string, { revision: number; promise: Promise<void> }>());
  const observed = useRef<{ clipId: string | null; json: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const send = useCallback(async (write: Write, keepalive = false) => {
    const id = write.clipId;
    const data = { videoEffects: write.videoEffects, videoEffectsRevision: write.revision };
    let rejectedRevision: number | undefined;
    try {
      const result = await mutate({ variables: { id, data }, ...(keepalive ? { context: { fetchOptions: { keepalive: true } } } : {}) });
      if ((acknowledged.current.get(id) ?? 0) >= write.revision) return;
      if (latest.current.get(id)?.revision !== write.revision) return;
      const saved = result.data?.updateClipData?.data;
      if (saved?.videoEffectsRevision !== write.revision || JSON.stringify(normalizeVideoEffects(saved?.videoEffects)) !== JSON.stringify(normalizeVideoEffects(write.videoEffects))) {
        if (Number.isSafeInteger(saved?.videoEffectsRevision) && saved.videoEffectsRevision > 0) rejectedRevision = saved.videoEffectsRevision;
        throw new Error('Effects save was not accepted');
      }
      acknowledged.current.set(id, write.revision);
      if (latest.current.get(id)?.revision === write.revision) {
        onSavedRef.current?.(id, data);
        setError(false);
      }
    } catch (cause) {
      // Superseded requests must neither restore an older pending write nor
      // report a failure after the latest snapshot was saved successfully.
      if ((acknowledged.current.get(id) ?? 0) >= write.revision || latest.current.get(id)?.revision !== write.revision || (!keepalive && (keepalives.current.get(id)?.revision ?? 0) >= write.revision)) return;
      // A rejected revision is distinct from a network retry: preserve the
      // draft, but let the user's next Retry advance beyond the server value.
      const retry = rejectedRevision === undefined ? write : { ...write, revision: nextRevision(rejectedRevision) };
      if (!pending.current.has(id)) pending.current.set(id, retry);
      latest.current.set(id, retry);
      setError(true);
      throw cause;
    }
  }, [mutate]);
  const drain = useCallback(async (id: string) => {
    const existing = inFlight.current.get(id);
    if (existing) return existing;
    const run = async () => {
      while (pending.current.has(id)) {
        const write = pending.current.get(id)!;
        pending.current.delete(id);
        await send(write);
      }
    };
    const promise = run().finally(() => { inFlight.current.delete(id); });
    inFlight.current.set(id, promise);
    return promise;
  }, [send]);
  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    await Promise.all([
      ...[...new Set([...pending.current.keys(), ...inFlight.current.keys()])].map(drain),
      ...[...keepalives.current.values()].map(entry => entry.promise),
    ]);
  }, [drain]);
  const fire = useCallback(() => { void flush().catch(() => {}); }, [flush]);
  const flushOnHide = useCallback(() => {
    clearTimeout(timer.current);
    const ids = new Set([...pending.current.keys(), ...inFlight.current.keys()]);
    for (const id of ids) {
      const write = latest.current.get(id);
      if (!write || (acknowledged.current.get(id) ?? 0) >= write.revision || keepalives.current.get(id)?.revision === write.revision) continue;
      pending.current.delete(id);
      const promise = send(write, true).finally(() => {
        if (keepalives.current.get(id)?.revision === write.revision) keepalives.current.delete(id);
      });
      keepalives.current.set(id, { revision: write.revision, promise });
      void promise.catch(() => {});
    }
  }, [send]);
  useFlushOnPageHide(flushOnHide);
  const fireRef = useRef(fire);
  fireRef.current = fire;
  useEffect(() => {
    if (observed.current?.clipId !== clipId) { fire(); observed.current = null; }
    if (!enabled || !clipId || loadedClipId !== clipId) return;
    const json = JSON.stringify(videoEffects);
    if (!observed.current) { observed.current = { clipId, json }; return; }
    if (observed.current.json === json) return;
    observed.current = { clipId, json };
    const write = { clipId, videoEffects, revision: nextRevision(loadedRevision) };
    pending.current.set(clipId, write);
    latest.current.set(clipId, write);
    clearTimeout(timer.current);
    timer.current = setTimeout(fire, delay);
    return () => { clearTimeout(timer.current); };
  }, [clipId, loadedClipId, loadedRevision, enabled, videoEffects, delay, fire]);
  useEffect(() => () => fireRef.current(), []);
  return { saving, error, flush };
}
